import type { Translate } from 'types'

import { noop } from './immutabilityHelpers'

export const DEFAULT_PRECISION = 2
export const DEFAULT_CURRENCY = 'PLN'

export const currencyToShort = (currencyCode: string) => {
  switch (currencyCode) {
    case DEFAULT_CURRENCY:
      return 'zł'
    case 'USD':
      return '$'
    case 'EUR':
      return '€'
    default:
      return currencyCode
  }
}

const THOUSANDS_LIMIT = 1000
const MILLIONS_LIMIT = 1000000

export const formatNumberWithoutDecimals = (number: number, translate: Translate) => {
  const formattedNumber = Number.parseInt(formatNumber(number, translate), 10)
  const formattedLabel = getAmountLabelForNumber(number, translate, '')

  return `${formattedNumber} ${formattedLabel}`
}

interface FormatNumberWithCurrency {
  currency?: string
  number?: string | number | null
  precision?: number
  translate: Translate
}

export const formatNumberWithCurrency = ({
  currency = DEFAULT_CURRENCY,
  number,
  precision = DEFAULT_PRECISION,
  translate,
}: FormatNumberWithCurrency) => {
  const formattedNumber = formatNumber(number, translate, precision)
  const formattedLabel = getAmountLabelForNumber(number, translate, currency)

  return `${formattedNumber} ${formattedLabel}`
}

export const formatNumber = (number?: string | number | null, translate: Translate = noop, precision = DEFAULT_PRECISION) => {
  const toLocaleOptions = {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }

  if (!number && number !== 0) {
    return '0'
  }

  if (number > MILLIONS_LIMIT || number < -MILLIONS_LIMIT) {
    return parseFloat(`${+number / 1000000}`).toLocaleString(translate('numberLocale'), toLocaleOptions)
  }

  if (number > THOUSANDS_LIMIT || number < -THOUSANDS_LIMIT) {
    return parseFloat(`${+number / 1000}`).toLocaleString(translate('numberLocale'), toLocaleOptions)
  }

  return number.toLocaleString(translate('numberLocale'), toLocaleOptions)
}

export const getAmountLabelForNumber = (
  number?: string | number | null,
  translate: Translate = noop,
  currency = DEFAULT_CURRENCY,
) => {
  const shortCurrency = currencyToShort(currency)

  if (!number && number !== 0) {
    return ''
  }

  if (number > MILLIONS_LIMIT || number < -MILLIONS_LIMIT) {
    return `${translate('millions', { currency: shortCurrency })}`
  }

  if (number > THOUSANDS_LIMIT || number < -THOUSANDS_LIMIT) {
    return `${translate('thousands', { currency: shortCurrency })}`
  }

  return shortCurrency
}

export const formatUnits = (number: number, translate: Translate, currency = '') => {
  const formattedNumber = formatNumber(number, translate, 0)
  const units = getAmountLabelForNumber(number, translate, currency)

  return `${formattedNumber} ${units}`.trim()
}

interface GetNumberWithCurrency {
  currency?: string
  number?: string | number | null
  precision?: number
  translate: Translate
}

export const getNumberWithCurrency = ({
  currency = DEFAULT_CURRENCY,
  number,
  precision = DEFAULT_PRECISION,
  translate,
}: GetNumberWithCurrency) => {
  const toLocaleOptions = { minimumFractionDigits: precision, maximumFractionDigits: precision }

  return typeof number === 'object' || typeof number === 'undefined'
    ? '-'
    : `${number.toLocaleString(translate('numberLocale'), toLocaleOptions)} ${currencyToShort(currency)}`
}
